'use client';

import { LeadForm } from "@plura/shared/types";
import {
  buildRedirectUrl,
  dynamicQuestionContent,
  getCurrentResponseScore,
  normalizeRedirectUrl,
  parseRedirectParams,
} from "@plura/shared/lead-forms";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from 'react-hook-form';
import "./conversation-redirect-screen.css";

interface Props {
  id: string;
  leadForm: LeadForm;
  /** The redirect destination, stored on the block's `content` field. */
  url: string | null;
  /** Query params, stored as JSON on the block's `secondaryContent` field. */
  paramsRaw: string | null;
  editorMode: boolean;
  previewMode: boolean;
  onContentClick?: (fieldName: string, blockId: string) => void;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
}

const ConversationRedirectScreen = ({ id, leadForm, url, paramsRaw, editorMode, previewMode, onContentClick, hiddenFields }: Props) => {
  const form = useFormContext();
  const { conversationId, ...userResponses } = form.getValues();
  const currentResponseScore = userResponses
    ? getCurrentResponseScore(userResponses, leadForm)
    : 0;
  const hasRedirectedRef = useRef(false);

  const rawUrl = (url ?? '').trim();
  const hasUrl = rawUrl.length > 0;

  // Resolve recall tokens with real answers (live), no markup.
  const resolveLive = (content: string) => dynamicQuestionContent({
    content,
    score: currentResponseScore,
    editMode: false,
    previewMode: false,
    hiddenFields,
    journeyItems: leadForm.journeyItems ?? [],
    userResponses,
  });

  const resolvedParams = parseRedirectParams(paramsRaw).map(p => ({
    name: p.name,
    value: resolveLive(p.value),
  }));

  const finalUrl = hasUrl ? buildRedirectUrl(resolveLive(rawUrl), resolvedParams) : '';

  // Human-friendly destination for the "Redirecting you to …" line.
  const displayDestination = (() => {
    if (!hasUrl) return '';
    try {
      return new URL(normalizeRedirectUrl(rawUrl)).host || rawUrl;
    } catch {
      return rawUrl;
    }
  })();

  const isLive = !editorMode && !previewMode;
  const [secondsLeft, setSecondsLeft] = useState(3);

  useEffect(() => {
    if (!isLive || hasRedirectedRef.current || !finalUrl) return;

    const countdown = setInterval(() => {
      setSecondsLeft(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    const timeout = setTimeout(() => {
      hasRedirectedRef.current = true;
      redirect(finalUrl);
    }, 3000);

    return () => {
      clearInterval(countdown);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive, finalUrl]);

  const handleContentClick = () => {
    if (!editorMode || !onContentClick) return;
    onContentClick('content', id);
  };

  return (
    <div className="stepHeaderWrapper redirectScreen">
      <motion.div
        className="redirectSpinner"
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className="redirectSpinnerRing" />
        <svg className="redirectSpinnerArrow" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 17 17 7M17 7H8M17 7v9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </motion.div>

      <motion.div
        key={`${id}-title`}
        exit={{ opacity: 0, transform: 'translateY(-12px)' }}
        initial={{ opacity: 0, transform: 'translateY(12px)' }}
        animate={{ opacity: 1, transform: 'translateY(0)' }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        onClick={handleContentClick}
      >
        {hasUrl ? (
          <h1>
            Redirecting you to <span className="redirectDestination">{displayDestination}</span>
          </h1>
        ) : (
          <h1>{editorMode ? 'Add a redirect URL in the settings panel' : 'Redirecting you…'}</h1>
        )}
      </motion.div>

      {isLive && hasUrl && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeInOut', delay: 0.2 }}
        >
          <h4>
            Taking you there in {secondsLeft || 0}s.{' '}
            <a href={finalUrl} className="redirectManualLink" onClick={() => { hasRedirectedRef.current = true; }}>Go now</a>
          </h4>
        </motion.div>
      )}
    </div>
  );
};

/**
 * Navigate to the destination, accounting for embedding:
 * - Standalone: navigate the tab.
 * - Embedded iframe: notify the parent (our embed script listens for
 *   `formsuite:redirect` and navigates the host page). If the top window is
 *   same-origin we navigate it directly; for cross-origin third-party embeds we
 *   fall back to navigating the iframe itself once the parent has had a chance.
 */
function redirect(finalUrl: string) {
  const inIframe = typeof window !== 'undefined' && window.self !== window.top;

  if (!inIframe) {
    window.location.assign(finalUrl);
    return;
  }

  try {
    window.parent.postMessage({ type: 'formsuite:redirect', url: finalUrl }, '*');
  } catch {
    // Ignore — parent may be cross-origin without a listener.
  }

  try {
    if (window.top && window.top.location) {
      window.top.location.href = finalUrl;
      return;
    }
  } catch {
    // Cross-origin: cannot touch the top window. Fall through to the delayed
    // iframe fallback so our embed parent can navigate the host page first.
  }

  setTimeout(() => {
    window.location.href = finalUrl;
  }, 800);
}

export default ConversationRedirectScreen;
